import type { Percent, ReadonlyVec2, SceneId } from "./types";

export type SignalSubscriber<T> = (value: T, previous: T) => void;
export type Unsubscribe = () => void;

/** Минимальный реактивный signal с подписками. */
export class Signal<T> {
  private current: T;
  private subscribers = new Set<SignalSubscriber<T>>();

  constructor(initial: T) {
    this.current = initial;
  }

  /** Текущее значение без подписки (no tracking). */
  peek(): T {
    return this.current;
  }

  /** Чтение значения; в будущем — auto-tracking через effect-scope. */
  get value(): T {
    return this.current;
  }

  /** Устанавливает новое значение и уведомляет подписчиков (skip if same). */
  set(next: T): void {
    if (Object.is(next, this.current)) return;
    const prev = this.current;
    this.current = next;
    for (const s of this.subscribers) s(next, prev);
  }

  /** Обновляет значение через функцию-трансформер. */
  update(fn: (current: T) => T): void {
    this.set(fn(this.current));
  }

  /** Подписывается на изменения, возвращает функцию отписки. */
  subscribe(fn: SignalSubscriber<T>): Unsubscribe {
    this.subscribers.add(fn);
    return () => {
      this.subscribers.delete(fn);
    };
  }
}

/** Состояние игры. Каждое поле — Signal для реактивного рендера. */
export interface GameState {
  readonly player: Signal<ReadonlyVec2>;
  readonly velocity: Signal<ReadonlyVec2>;
  readonly energy: Signal<Percent>;
  readonly glow: Signal<boolean>;
  readonly seedsCollected: Signal<number>;
  readonly sceneId: Signal<SceneId>;
  readonly complete: Signal<boolean>;
}

/** Фабрика дефолтного состояния — используется в bootstrap и тестах. */
export const createGameState = (): GameState => {
  const zero: ReadonlyVec2 = { x: 0, y: 0 };
  return {
    player: new Signal<ReadonlyVec2>({ x: 50, y: 70 }),
    velocity: new Signal<ReadonlyVec2>(zero),
    energy: new Signal(100 as Percent),
    glow: new Signal(false),
    seedsCollected: new Signal(0),
    sceneId: new Signal<SceneId>("splash"),
    complete: new Signal(false),
  };
};
